import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Papa } from 'ngx-papaparse';
import { ImportResult } from '../upload-accessibility-checks.component';

@Component({
    selector: 'app-upload-results',
    imports: [CommonModule],
    templateUrl: './upload-results.component.html',
    styleUrls: ['./upload-results.component.scss'],
})
export class UploadResultsComponent {
    @Input() result: ImportResult | null = null;

    constructor (private papa: Papa) {}

    getSuccessRate (): number {
        if (!this.result || this.result.total === 0) {
            return 0;
        }
        return Math.round((this.result.successful / this.result.total) * 100);
    }

    downloadErrorReport (): void {
        if (!this.result || this.result.errors.length === 0) {
            return;
        }

        const csvData = this.result.errors.map(error => ({
            'Row Number': error.rowNumber,
            'Database ID': error.id,
            'Error': error.error,
        }));

        const csv = this.papa.unparse(csvData, {
            quotes: true,
            header: true,
        });

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `import-errors-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    downloadDetailedReport (): void {
        if (!this.result) {
            return;
        }

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('.')[0];
        const reportContent = this.generateDetailedReport();

        const blob = new Blob([reportContent], { type: 'text/plain;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `import-report-${timestamp}.txt`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    private generateDetailedReport (): string {
        if (!this.result) {
            return '';
        }

        const timestamp = new Date().toISOString();
        const lines: string[] = [];

        lines.push('ACCESSIBILITY CHECKS IMPORT REPORT');
        lines.push('==================================');
        lines.push(`Generated: ${timestamp}`);
        lines.push('');

        lines.push('SUMMARY');
        lines.push('-------');
        lines.push(`Total Rows Processed: ${this.result.total}`);
        lines.push(`Successfully Updated: ${this.result.successful}`);
        lines.push(`Failed Updates: ${this.result.failed}`);
        lines.push(`Success Rate: ${this.getSuccessRate()}%`);
        lines.push('');

        if (this.result.successful > 0) {
            lines.push('SUCCESSFUL UPDATES');
            lines.push('------------------');
            lines.push(`${this.result.successful} accessibility checks were successfully updated in the database:\n`);

            this.result.successfulUpdates.forEach((update, index) => {
                lines.push(`${index + 1}. Row ${update.rowNumber}`);
                lines.push(`   Database ID: ${update.id}`);
                if (update.name) {
                    lines.push(`   Check Name: ${update.name}`);
                }
                lines.push('');
            });
        }

        if (this.result.errors.length > 0) {
            lines.push('ERRORS');
            lines.push('------');
            lines.push(`The following ${this.result.errors.length} rows encountered errors during import:\n`);

            this.result.errors.forEach((error, index) => {
                lines.push(`${index + 1}. Row ${error.rowNumber}`);
                lines.push(`   Database ID: ${error.id}`);
                lines.push(`   Error: ${error.error}`);
                lines.push('');
            });
        }

        lines.push('END OF REPORT');

        return lines.join('\n');
    }
}
